import React, { useEffect, useRef } from 'react';
import { Animated, Easing, Image, Platform, StyleSheet, Text, View } from 'react-native';
import { Coins } from 'components/Icons';

import { useRemScale } from 'lib/remScale';
import { TYPE, FONTS, TITLE_SIZE } from 'style/typography';
import { playCoins } from 'firstVisit/coinSound';
import { flightToStore } from 'firstVisit/targets';

const HOLD_MS = 1900;
const BURST = [-150, -100, -45, 10, 60, 115, 160];

/**
 * A badge earned: it flies in, shows its name and the QuikCoins it paid with a burst of coins and the coin sound,
 * then flies into the Collection Store where it now lives (Sol player-tour Q4 A). Never blocks the world.
 */
export default function BadgeMoment({ badge, onDone }) {
  const { rem } = useRemScale();
  const enter = useRef(new Animated.Value(0)).current;
  const coins = useRef(new Animated.Value(0)).current;
  const leave = useRef(new Animated.Value(0)).current;
  const flight = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (!badge) return undefined;
    enter.setValue(0);
    coins.setValue(0);
    leave.setValue(0);
    flight.current = flightToStore();
    let stopped = false;
    const timer = setTimeout(() => {
      if (stopped) return;
      Animated.timing(leave, {
        toValue: 1,
        duration: 650,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: Platform.OS !== 'web',
      }).start(() => !stopped && onDone && onDone());
    }, HOLD_MS);
    Animated.spring(enter, { toValue: 1, friction: 6, tension: 70, useNativeDriver: Platform.OS !== 'web' }).start();
    const coinTimer = setTimeout(() => {
      if (stopped) return;
      if (badge.coins > 0) playCoins(3);
      Animated.timing(coins, {
        toValue: 1,
        duration: 900,
        easing: Easing.out(Easing.quad),
        useNativeDriver: Platform.OS !== 'web',
      }).start();
    }, 380);
    return () => {
      stopped = true;
      clearTimeout(timer);
      clearTimeout(coinTimer);
    };
  }, [badge, coins, enter, leave, onDone]);

  if (!badge) return null;

  const size = rem(150);
  const scale = Animated.multiply(
    enter.interpolate({ inputRange: [0, 1], outputRange: [0.35, 1] }),
    leave.interpolate({ inputRange: [0, 1], outputRange: [1, 0.22] }),
  );
  const translateX = leave.interpolate({ inputRange: [0, 1], outputRange: [0, flight.current.x] });
  const translateY = Animated.add(
    enter.interpolate({ inputRange: [0, 1], outputRange: [rem(60), 0] }),
    leave.interpolate({ inputRange: [0, 1], outputRange: [0, flight.current.y] }),
  );
  const opacity = Animated.multiply(enter, leave.interpolate({ inputRange: [0, 0.85, 1], outputRange: [1, 1, 0] }));
  const textOpacity = Animated.multiply(enter, leave.interpolate({ inputRange: [0, 0.3], outputRange: [1, 0], extrapolate: 'clamp' }));

  return (
    <View style={styles.overlay} pointerEvents="none" testID="badge-moment">
      <Animated.View style={{ alignItems: 'center', opacity, transform: [{ translateX }, { translateY }, { scale }] }}>
        <View style={[styles.glow, { width: size * 1.35, height: size * 1.35, borderRadius: size }]} />
        {badge.imageUrl ? (
          <Image source={{ uri: badge.imageUrl }} style={{ width: size, height: size }} resizeMode="contain" />
        ) : (
          <View style={[styles.placeholder, { width: size, height: size, borderRadius: size / 2 }]} />
        )}
      </Animated.View>
      <Animated.View style={{ alignItems: 'center', marginTop: rem(14), opacity: textOpacity }}>
        <Text style={[styles.kicker, { fontSize: rem(TYPE.sm), fontFamily: FONTS.body }]}>NEW BADGE</Text>
        <Text style={[styles.name, { fontSize: rem(TITLE_SIZE), fontFamily: FONTS.heading }]}>{badge.name}</Text>
        {badge.coins > 0 ? (
          <View style={[styles.coinsRow, { marginTop: rem(8), paddingHorizontal: rem(14), paddingVertical: rem(6), borderRadius: rem(20) }]}>
            <Coins size={rem(18)} color="#C5E836" strokeWidth={2.5} />
            <Text style={[styles.coinsText, { fontSize: rem(TYPE.base), fontFamily: FONTS.body, marginLeft: rem(6) }]}>
              {`+${badge.coins} QuikCoins`}
            </Text>
          </View>
        ) : null}
      </Animated.View>
      {badge.coins > 0
        ? BURST.map((dx, i) => (
            <Animated.View
              key={i}
              style={[
                styles.coin,
                {
                  width: rem(18),
                  height: rem(18),
                  borderRadius: rem(9),
                  opacity: coins.interpolate({ inputRange: [0, 0.15, 0.8, 1], outputRange: [0, 1, 1, 0] }),
                  transform: [
                    { translateX: coins.interpolate({ inputRange: [0, 1], outputRange: [0, rem(dx)] }) },
                    { translateY: coins.interpolate({ inputRange: [0, 0.45, 1], outputRange: [rem(40), rem(-70 - (i % 3) * 18), rem(10)] }) },
                  ],
                },
              ]}
            />
          ))
        : null}
    </View>
  );
}

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 90,
  },
  glow: {
    position: 'absolute',
    alignSelf: 'center',
    top: '-17%',
    backgroundColor: 'rgba(197,232,54,0.18)',
  },
  placeholder: { backgroundColor: 'rgba(197,232,54,0.25)' },
  kicker: { color: '#C5E836', fontWeight: '700', letterSpacing: 2 },
  name: { color: '#F6FBFF', fontWeight: '700', textAlign: 'center', marginTop: 2 },
  coinsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(15,16,20,0.85)',
    borderWidth: 1,
    borderColor: 'rgba(197,232,54,0.35)',
  },
  coinsText: { color: '#C5E836', fontWeight: '700' },
  coin: {
    position: 'absolute',
    backgroundColor: '#F5C542',
    borderWidth: 2,
    borderColor: '#B8860B',
  },
});
